import React, { useEffect, useState } from "react";
import { ref, get } from "firebase/database";
import { db } from "@react-native-firebase/database";
import { getLocalStorageItem } from "core/helpers";
import { TOTAL_CASUAL_LEAVE, TOTAL_SICK_LEAVE } from "res/constants";
import StyleSheetRW from "core/StyleSheetRW";
import crossIcon from "res/svgs/cross.svg";
import { formatDaysAndHours, formatTotalHours } from "src/utils/TimeOffRequestUtils";

interface UserLeaveSummaryProps {
    onClose: () => void;
    onOpen: boolean;
}

const UserLeaveSummary: React.FC<UserLeaveSummaryProps> = ({ onClose, onOpen }) => {
    const [totalAvailedCasualLeaves, setTotalAvailedCasualLeaves] = useState(0);
    const [currentMonthTotalAvailedCasualLeaves, setCurrentMonthTotalAvailedCasualLeaves] = useState(0);
    const [currentMonthTotalAvailedSickLeaves, setCurrentMonthTotalAvailedSickLeaves] = useState(0);
    const [totalAvailedSickLeaves, setTotalAvailedSickLeaves] = useState(0);
    const loggedinUser = getLocalStorageItem("loggedinUser");
    const [userLeaveData, setUserLeaveData] = useState({
        remainingCasualLeaves: 0,
        remainingSickLeaves: 0,
        employmentTypeHour: 8,
        employmentType: "Full Time"
    });

    const fetchUserLeaveData = async () => {
        if (loggedinUser) {
            const dbRef = ref(db, `profile/${loggedinUser.uid}`);
            const snapshot = await get(dbRef);
            if (snapshot.exists()) {
                setUserLeaveData({
                    ...userLeaveData,
                    remainingCasualLeaves: snapshot.val().remainingCasualLeaves || 0,
                    remainingSickLeaves: snapshot.val().remainingSickLeaves || 0,
                    employmentTypeHour: snapshot.val().employmentType === "Full Time" ? 8 : 4,
                    employmentType: snapshot.val().employmentType
                });
            }
        }
    };

    useEffect(() => {
        fetchUserLeaveData();
    }, [onOpen]);

    const isCurrentMonth = (dateString: string) => {
        const date = new Date(dateString);
        const now = new Date();
        return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
    };

    useEffect(() => {
        const fetchLeaveData = async () => {
            let approvedCasualLeaves = 0;
            let approvedSickLeaves = 0;
            let currentMonthApprovedCasualLeaves = 0;
            let currentMonthApprovedSickLeaves = 0;

            try {
                if (loggedinUser) {
                    const dbRef = ref(db, `timeOffRequest/${loggedinUser.uid}`);
                    const snapshot = await get(dbRef);
                    if (snapshot.exists()) {
                        const data = snapshot.val();
                        Object.values(data).forEach((leaves: any) => {
                            Object.values(leaves).forEach((leave: any) => {
                                if (leave.status === "Approved") {
                                    if (isCurrentMonth(leave.startDate)) {
                                        if (leave.leaveType === "Casual leaves") {
                                            const currentMonthLeaveValue = leave.noOfDays !== "0" ? leave.noOfDays : formatTotalHours(leave.totalhours, userLeaveData.employmentTypeHour);
                                            currentMonthApprovedCasualLeaves += parseFloat(currentMonthLeaveValue);
                                        } else if (leave.leaveType === "Sick leaves") {
                                            const currentMonthLeaveValue = leave.noOfDays !== "0" ? leave.noOfDays : formatTotalHours(leave.totalhours, userLeaveData.employmentTypeHour);
                                            currentMonthApprovedSickLeaves += parseFloat(currentMonthLeaveValue);
                                        }
                                    }

                                    if (leave.leaveType === "Casual leaves") {
                                        const leaveValue = leave.noOfDays !== "0" ? leave.noOfDays : formatTotalHours(leave.totalhours, userLeaveData.employmentTypeHour);
                                        approvedCasualLeaves += parseFloat(leaveValue);
                                    } else if (leave.leaveType === "Sick leaves") {
                                        const leaveValue = leave.noOfDays !== "0" ? leave.noOfDays : formatTotalHours(leave.totalhours, userLeaveData.employmentTypeHour);
                                        approvedSickLeaves += parseFloat(leaveValue);
                                    }
                                }
                            });
                        });
                        setCurrentMonthTotalAvailedCasualLeaves(currentMonthApprovedCasualLeaves);
                        setCurrentMonthTotalAvailedSickLeaves(currentMonthApprovedSickLeaves);
                        setTotalAvailedCasualLeaves(approvedCasualLeaves);
                        setTotalAvailedSickLeaves(approvedSickLeaves);
                    }
                }
            } catch (error) {
                console.error("Error fetching leave data: ", error);
            }
        };

        fetchLeaveData();
    }, [onOpen]);

    const remainingCasualLeaves = (userLeaveData.remainingCasualLeaves !== 0 ? userLeaveData.remainingCasualLeaves : TOTAL_CASUAL_LEAVE) - totalAvailedCasualLeaves;
    const remainingSickLeaves = TOTAL_SICK_LEAVE - totalAvailedSickLeaves;

    return (
        <>
            {onOpen && (
                <div style={styles.overlay}>
                    <div style={styles.dialog}>
                        <h2 style={styles.heading}>Your Leave Summary</h2>
                        <button style={styles.closeButton} onClick={onClose}>
                            <img src={crossIcon} alt="Close" style={{ width: 13, height: 13 }} />
                        </button>
                        <div style={styles.container}>
                            <h2 style={styles.header}>Casual leaves</h2>
                            <table style={styles.table}>
                                <tbody>
                                    <tr>
                                        <td style={styles.cell}>Total Available:</td>
                                        <td style={styles.cell}>{formatDaysAndHours(userLeaveData.remainingCasualLeaves !== 0 ? userLeaveData.remainingCasualLeaves : TOTAL_CASUAL_LEAVE, userLeaveData.employmentType)}</td>
                                    </tr>
                                    <tr style={styles.rowEven}>
                                        <td style={styles.cell}>Current Month Availed:</td>
                                        <td style={styles.cell}>{formatDaysAndHours(currentMonthTotalAvailedCasualLeaves, userLeaveData.employmentType)}</td>
                                    </tr>
                                    <tr style={styles.rowEven}>
                                        <td style={styles.cell}>Total Availed:</td>
                                        <td style={styles.cell}>{formatDaysAndHours(totalAvailedCasualLeaves, userLeaveData.employmentType)}</td>
                                    </tr>
                                    <tr>
                                        <td style={styles.cell}>Remaining Leaves:</td>
                                        <td style={styles.cell}>{formatDaysAndHours(remainingCasualLeaves, userLeaveData.employmentType)}</td>
                                    </tr>
                                    <tr style={styles.rowEven}>
                                        <td style={styles.cell}>Leave Update Type:</td>
                                        <td style={styles.cell}>Per Year Update</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <div style={styles.container}>
                            <h2 style={styles.header}>Sick leaves</h2>
                            <table style={styles.table}>
                                <tbody>
                                    <tr>
                                        <td style={styles.cell}>Total Available:</td>
                                        <td style={styles.cell}>{formatDaysAndHours(userLeaveData.remainingCasualLeaves !== 0 ? userLeaveData.remainingSickLeaves : TOTAL_SICK_LEAVE, userLeaveData.employmentType)}</td>
                                    </tr>
                                    <tr style={styles.rowEven}>
                                        <td style={styles.cell}>Current Month Availed:</td>
                                        <td style={styles.cell}>{formatDaysAndHours(currentMonthTotalAvailedSickLeaves, userLeaveData.employmentType)}</td>
                                    </tr>
                                    <tr style={styles.rowEven}>
                                        <td style={styles.cell}>Total Availed:</td>
                                        <td style={styles.cell}>{formatDaysAndHours(totalAvailedSickLeaves, userLeaveData.employmentType)}</td>
                                    </tr>
                                    <tr>
                                        <td style={styles.cell}>Remaining Leaves:</td>
                                        <td style={styles.cell}>{formatDaysAndHours(remainingSickLeaves, userLeaveData.employmentType)}</td>
                                    </tr>
                                    <tr style={styles.rowEven}>
                                        <td style={styles.cell}>Leave Update Type:</td>
                                        <td style={styles.cell}>Per Year Update</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

const styles = StyleSheetRW.create(() => ({
    overlay: {
        position: 'absolute',
        zIndex: 1,
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: 'rgba(0,0,0,0.5)',
        overflow: 'hidden',
    },
    dialog: {
        backgroundColor: 'white',
        padding: '20px',
        borderRadius: 8,
        width: "349px",
        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
        position: "relative"
    },
    heading: {
        margin: 0,
        marginBottom: 10,
        fontFamily: "Outfit",
        fontSize: 20,
        fontWeight: "bold",
        textAlign: "center",
        color: "#333333",
    },
    closeButton: {
        position: "absolute",
        top: "25px",
        right: "10px",
        backgroundColor: "transparent",
        border: "none",
        cursor: "pointer",
    },
    container: {
        width: 'auto',
        margin: '10px auto',
        fontFamily: 'Outfit',
        borderCollapse: 'collapse',
        border: '1px solid #ddd',
    },
    header: {
        textAlign: 'center',
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse',
    },
    cell: {
        border: '1px solid #ddd',
        padding: '8px',
    },
    rowEven: {
        backgroundColor: '#f9f9f9',
    },
    headerCell: {
        paddingTop: '12px',
        paddingBottom: '12px',
        textAlign: 'left',
        backgroundColor: '#4CAF50',
        color: 'white',
    },
}));

export default UserLeaveSummary;